// ---------------------------------------------------------------------------
// Generation orphan copy (G1 — honest labeling of unfinished generations)
//
// A chat-generated project whose settings.generation is still 'pending' past
// the 10-minute threshold died mid-generation (browser session lost between
// create and persist). The dashboard must say so plainly — "생성이 완료되지
// 않았습니다" — instead of disguising the stub as a normal draft. Same
// Record<LocaleCode, …> pattern as coogo-studio-home-renewal-copy.ts.
// ---------------------------------------------------------------------------

import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

export interface GenerationOrphanCopy {
  /** Headline label — the honest state, not "draft". */
  label: string;
  /** One-line explanation of what happened and what the actions do. */
  detail: string;
  /** CTA that reopens the project through the existing open flow (the
   * stored-job reconnect surface lives there); NOT a resume engine. F7 —
   * the copy must say so honestly: open-and-review wording, never a
   * "continue generating" promise the handler cannot keep. */
  resume: string;
  /** CTA that routes into the existing delete path. */
  deleteAction: string;
}

const COPY: Record<LocaleCode, GenerationOrphanCopy> = {
  en: {
    label: "Generation didn't finish",
    detail:
      "This project was created but its generation never completed. Open it to continue, or delete it.",
    resume: "Open and review",
    deleteAction: "Delete",
  },
  ko: {
    label: "생성이 완료되지 않았습니다",
    detail:
      "프로젝트는 만들어졌지만 생성이 끝까지 진행되지 않았어요. 열어서 이어가거나 삭제할 수 있어요.",
    resume: "열어서 확인",
    deleteAction: "삭제",
  },
  ja: {
    label: "生成が完了しませんでした",
    detail:
      "プロジェクトは作成されましたが、生成が最後まで完了していません。開いて続けるか、削除できます。",
    resume: "開いて確認",
    deleteAction: "削除",
  },
  zh: {
    label: "生成未完成",
    detail: "项目已创建，但生成未能完成。可以打开继续，或将其删除。",
    resume: "打开查看",
    deleteAction: "删除",
  },
  "zh-TW": {
    label: "生成未完成",
    detail: "專案已建立，但生成未能完成。可以開啟繼續，或將其刪除。",
    resume: "開啟查看",
    deleteAction: "刪除",
  },
};

export function getGenerationOrphanCopy(
  locale: LocaleCode,
): GenerationOrphanCopy {
  return COPY[locale] ?? COPY.en;
}
